import { CanvasTexture, RepeatWrapping, SRGBColorSpace, Texture } from 'three';

/**
 * Procedurally drawn surfaces for the simulated room.
 *
 * Procedural rather than image assets because neither repo ships any, and adding binaries to every
 * Designer and JUTE build for a preview backdrop is a poor trade. Everything here is drawn once, at
 * module scale, and reused.
 *
 * The guiding constraint is that a real surface is never uniform. Flat fills read as printed paper
 * however good the geometry is, so the work is all in mottling, edge wear and large-scale grime.
 */

/** Deterministic PRNG, so the wear pattern is identical every run rather than shimmering per render. */
function makeRandom(seed: number): () => number {
	let state = seed;
	return () => {
		state = (state * 1664525 + 1013904223) % 4294967296;
		return state / 4294967296;
	};
}

/** `roundRect` is recent enough to be worth a fallback rather than a hard dependency. */
function roundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
	ctx.beginPath();
	if (typeof ctx.roundRect === 'function') {
		ctx.roundRect(x, y, w, h, r);
		return;
	}
	ctx.moveTo(x + r, y);
	ctx.arcTo(x + w, y, x + w, y + h, r);
	ctx.arcTo(x + w, y + h, x, y + h, r);
	ctx.arcTo(x, y + h, x, y, r);
	ctx.arcTo(x, y, x + w, y, r);
	ctx.closePath();
}

/** Fine per-pixel grain. The single most effective thing for stopping a fill looking like a fill. */
function addGrain(ctx: CanvasRenderingContext2D, w: number, h: number, strength: number, random: () => number): void {
	const image = ctx.getImageData(0, 0, w, h);
	const { data } = image;
	for (let i = 0; i < data.length; i += 4) {
		const n = (random() - 0.5) * strength;
		data[i] = Math.min(255, Math.max(0, data[i] + n));
		data[i + 1] = Math.min(255, Math.max(0, data[i + 1] + n));
		data[i + 2] = Math.min(255, Math.max(0, data[i + 2] + n));
	}
	ctx.putImageData(image, 0, 0);
}

/** Soft irregular patches — weathering, damp, unevenly worn paint. Large scale, very low contrast. */
function addPatches(
	ctx: CanvasRenderingContext2D,
	w: number,
	h: number,
	count: number,
	maxAlpha: number,
	random: () => number
): void {
	for (let i = 0; i < count; i++) {
		const x = random() * w;
		const y = random() * h;
		const radius = (0.12 + random() * 0.28) * w;
		const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
		const dark = random() > 0.4;
		const tone = dark ? '0, 0, 0' : '255, 255, 255';
		gradient.addColorStop(0, `rgba(${tone}, ${maxAlpha * random()})`);
		gradient.addColorStop(1, `rgba(${tone}, 0)`);
		ctx.fillStyle = gradient;
		ctx.fillRect(0, 0, w, h);
	}
}

/** A base tone, per channel, so a colourway can be a hue rather than a shade of grey. */
type RGB = readonly [number, number, number];

/** One brick or board's own tone: the palette base lifted by a per-piece amount, so no two match. */
function pieceTone([r, g, b]: RGB, spread: number, random: () => number): string {
	const lift = Math.round(random() * spread);
	return `rgb(${Math.min(255, r + lift)}, ${Math.min(255, g + lift)}, ${Math.min(255, b + lift)})`;
}

/**
 * The two colourways each surface is drawn in.
 *
 * Only the COLOUR map has one: paint does not change a brick's shape, so both relief passes are
 * drawn identically whichever way round the room is. Dark is a redraw rather than a tint over the
 * default one — multiplying a pastel down takes the mortar and the wear with it and lands on grey
 * mud, where these keep a dark brick against a still-readable joint.
 *
 * The default sits mid-tone rather than white so that neither pale nor bright content disappears
 * against the room it is being judged in.
 */
interface IBrickPalette {
	/** Mortar, showing through the gaps between bricks. */
	joint: string;
	/** Base tone of one brick, and how much it varies brick to brick. */
	base: RGB;
	spread: number;
	/** Blotching within a brick: deeper clay in places, and lime bloom over it. */
	mottleDark: string;
	mottleLight: string;
	/** Dirt gathering where a brick meets the mortar. */
	wear: string;
	/** A chipped corner, exposing paler unweathered clay. */
	chip: string;
}

/** Pastel clay: a dusty terracotta brick against pale warm mortar. */
const BRICK_LIGHT: IBrickPalette = {
	joint: '#cbc2b6',
	base: [196, 156, 142],
	spread: 20,
	mottleDark: '148, 102, 86',
	mottleLight: '255, 240, 232',
	wear: '130, 92, 78',
	chip: 'rgba(226, 202, 188, 0.5)'
};

const BRICK_DARK: IBrickPalette = {
	joint: '#3c3a37',
	base: [54, 52, 48],
	spread: 16,
	mottleDark: '16, 15, 14',
	mottleLight: '122, 116, 108',
	wear: '10, 9, 8',
	chip: 'rgba(104, 96, 88, 0.5)'
};

interface IPlankPalette {
	/** The seam under the boards, so the gaps between them are genuine gaps. */
	joint: string;
	base: RGB;
	spread: number;
	/** Grain and knots, both of which read as timber showing through the finish. */
	grain: string;
	knot: string;
	/** Finish thinning and dirt along a board's edges. */
	wear: string;
}

/**
 * Pastel pine: a soft creamy board with muted grain and knots.
 *
 * Pine rather than a hardwood because it is the easier softwood to read at a glance — frequent knots
 * and coarse bands, where oak's finer figure disappears at the distance this floor is orbited from.
 * Everything is held at low saturation and on one hue: at full timber chroma the floor reads as a
 * feature rather than a backdrop, which for a room whose whole job is showing off content is backwards.
 */
const PLANK_LIGHT: IPlankPalette = {
	joint: '#a2988a',
	base: [196, 182, 166],
	spread: 22,
	grain: '138, 122, 106',
	knot: '116, 102, 90',
	wear: '124, 110, 98'
};

const PLANK_DARK: IPlankPalette = {
	joint: '#2f2c28',
	base: [58, 56, 52],
	spread: 18,
	// Lighter than the boards, where the pastel room's are darker: over dark paint it is the grain that
	// catches what light there is, and a darker stroke on a dark board is simply invisible.
	grain: '152, 132, 106',
	knot: '146, 120, 92',
	wear: '10, 9, 8'
};

const BRICK_W = 1536;
const BRICK_H = 1024;
const COURSES = 4;
const BRICKS_PER_COURSE = 2;

/**
 * A brick wall — pastel clay, or painted dark when `dark` is set: a colour map plus a matching bump map.
 *
 * The tile is two bricks wide by four courses with alternate courses offset half a brick — a running
 * bond, and an even course count means it wraps cleanly, so the wall has no visible tile seam. The
 * canvas is 3:2 to match the tile's real proportions (450x300mm), which keeps texel density roughly
 * even across both axes.
 *
 * The bump map is drawn separately rather than reusing the colour map: mottling and grime should
 * change how a brick looks, not its shape, and only the mortar and the brick edges should actually
 * recede.
 */
export function createBrickTextures(repeatX: number, repeatY: number, dark = false): { map: Texture; bump: Texture } | null {
	const colour = document.createElement('canvas');
	const relief = document.createElement('canvas');
	colour.width = relief.width = BRICK_W;
	colour.height = relief.height = BRICK_H;
	const ctx = colour.getContext('2d');
	const bumpCtx = relief.getContext('2d');
	if (!ctx || !bumpCtx) return null;

	const random = makeRandom(20240818);
	const palette = dark ? BRICK_DARK : BRICK_LIGHT;
	const brickWidth = BRICK_W / BRICKS_PER_COURSE;
	const courseHeight = BRICK_H / COURSES;
	const mortar = 16;
	const corner = 7;

	// Mortar first, showing through the gaps rather than stroked on top: cheaper, and it gives the
	// bricks genuinely recessed edges instead of drawn-on outlines.
	ctx.fillStyle = palette.joint;
	ctx.fillRect(0, 0, BRICK_W, BRICK_H);
	bumpCtx.fillStyle = '#3a3a3a';
	bumpCtx.fillRect(0, 0, BRICK_W, BRICK_H);

	for (let row = 0; row < COURSES; row++) {
		const offset = row % 2 === 0 ? 0 : brickWidth / 2;
		// One extra brick each side so the half-brick offset still covers the tile edges.
		for (let col = -1; col <= BRICKS_PER_COURSE; col++) {
			// Real courses are not perfectly regular. A pixel or two of jitter reads as handmade.
			const jitterX = (random() - 0.5) * 4;
			const jitterY = (random() - 0.5) * 3;
			const x = col * brickWidth + offset + mortar / 2 + jitterX;
			const y = row * courseHeight + mortar / 2 + jitterY;
			const w = brickWidth - mortar;
			const h = courseHeight - mortar;

			// Clay is uneven, and no two bricks fired the same way.
			ctx.save();
			roundedRect(ctx, x, y, w, h, corner);
			ctx.clip();
			ctx.fillStyle = pieceTone(palette.base, palette.spread, random);
			ctx.fillRect(x, y, w, h);

			// Mottling within the brick — the surface weathers unevenly and deeper clay shows through.
			for (let i = 0; i < 26; i++) {
				const bx = x + random() * w;
				const by = y + random() * h;
				const r = (0.05 + random() * 0.22) * w;
				const blot = ctx.createRadialGradient(bx, by, 0, bx, by, r);
				const shade = random() > 0.5 ? palette.mottleDark : palette.mottleLight;
				blot.addColorStop(0, `rgba(${shade}, ${0.03 + random() * 0.09})`);
				blot.addColorStop(1, `rgba(${shade}, 0)`);
				ctx.fillStyle = blot;
				ctx.fillRect(x, y, w, h);
			}

			// Worn edges: bricks are darker and dirtier where they meet the mortar.
			const edge = ctx.createLinearGradient(x, y, x, y + h);
			edge.addColorStop(0, `rgba(${palette.wear}, 0.22)`);
			edge.addColorStop(0.18, `rgba(${palette.wear}, 0)`);
			edge.addColorStop(0.82, `rgba(${palette.wear}, 0)`);
			edge.addColorStop(1, `rgba(${palette.wear}, 0.28)`);
			ctx.fillStyle = edge;
			ctx.fillRect(x, y, w, h);

			// The occasional chipped corner.
			if (random() > 0.65) {
				const cx = random() > 0.5 ? x + w : x;
				const cy = random() > 0.5 ? y + h : y;
				ctx.fillStyle = palette.chip;
				ctx.beginPath();
				ctx.arc(cx, cy, 6 + random() * 12, 0, Math.PI * 2);
				ctx.fill();
			}
			ctx.restore();

			// Relief: the brick face stands proud of the mortar, with a soft shoulder at the edges.
			bumpCtx.save();
			roundedRect(bumpCtx, x, y, w, h, corner);
			bumpCtx.clip();
			bumpCtx.fillStyle = '#d8d8d8';
			bumpCtx.fillRect(x, y, w, h);
			const shoulder = bumpCtx.createLinearGradient(x, y, x, y + h);
			shoulder.addColorStop(0, 'rgba(0, 0, 0, 0.45)');
			shoulder.addColorStop(0.12, 'rgba(0, 0, 0, 0)');
			shoulder.addColorStop(0.88, 'rgba(0, 0, 0, 0)');
			shoulder.addColorStop(1, 'rgba(0, 0, 0, 0.45)');
			bumpCtx.fillStyle = shoulder;
			bumpCtx.fillRect(x, y, w, h);
			bumpCtx.restore();
		}
	}

	// Grime across the whole wall, ignoring brick boundaries — this is what stops the tile reading as
	// a repeating unit even though it is one.
	addPatches(ctx, BRICK_W, BRICK_H, 7, 0.1, random);
	addGrain(ctx, BRICK_W, BRICK_H, 14, random);
	addGrain(bumpCtx, BRICK_W, BRICK_H, 10, random);

	const map = new CanvasTexture(colour);
	const bump = new CanvasTexture(relief);
	for (const texture of [map, bump]) {
		texture.wrapS = texture.wrapT = RepeatWrapping;
		// Whole tiles only: a fractional repeat cuts a brick in half at the wrap and puts a visible
		// seam down the wall.
		texture.repeat.set(Math.max(1, Math.round(repeatX)), Math.max(1, Math.round(repeatY)));
		texture.anisotropy = 8;
	}
	map.colorSpace = SRGBColorSpace;
	return { map, bump };
}

const PLANK_W = 1024;
const PLANK_H = 1536;
const BOARDS_PER_TILE = 4;
/** Two rows per tile with a staggered join, so the boards read as finite lengths rather than stripes. */
const ROWS_PER_TILE = 2;

/**
 * A plank floor, pastel pine or painted dark: a colour map plus a matching bump map.
 *
 * Same construction as the brick — a tile that wraps, a separate relief pass, and wear rather than
 * pattern doing the work. Wood is harder than brick to make convincing because a finished board has
 * so little colour variation left, so almost everything here is about the grain and the knots showing
 * through it: without those it reads as stripes, whatever the proportions.
 *
 * Portrait tile (boards run down its length) with an end-join halfway, offset on alternate boards so
 * the joins never line up across the floor.
 */
export function createPlankTextures(repeatX: number, repeatY: number, dark = false): { map: Texture; bump: Texture } | null {
	const colour = document.createElement('canvas');
	const relief = document.createElement('canvas');
	colour.width = relief.width = PLANK_W;
	colour.height = relief.height = PLANK_H;
	const ctx = colour.getContext('2d');
	const bumpCtx = relief.getContext('2d');
	if (!ctx || !bumpCtx) return null;

	const random = makeRandom(19770101);
	const palette = dark ? PLANK_DARK : PLANK_LIGHT;
	const boardWidth = PLANK_W / BOARDS_PER_TILE;
	const rowHeight = PLANK_H / ROWS_PER_TILE;
	const seam = 4;

	// Seam colour underneath, so the gaps between boards are genuine gaps rather than drawn lines.
	ctx.fillStyle = palette.joint;
	ctx.fillRect(0, 0, PLANK_W, PLANK_H);
	bumpCtx.fillStyle = '#4a4a4a';
	bumpCtx.fillRect(0, 0, PLANK_W, PLANK_H);

	for (let col = 0; col < BOARDS_PER_TILE; col++) {
		// Alternate boards start half a row up, so end-joins stagger across the floor the way a real
		// boarded surface does. Both offsets wrap because there are two rows.
		const rowOffset = col % 2 === 0 ? 0 : rowHeight / 2;
		for (let row = -1; row <= ROWS_PER_TILE; row++) {
			const x = col * boardWidth + seam / 2;
			const y = row * rowHeight + rowOffset + seam / 2;
			const w = boardWidth - seam;
			const h = rowHeight - seam;

			ctx.save();
			ctx.beginPath();
			ctx.rect(x, y, w, h);
			ctx.clip();

			// Timber takes the finish differently board to board — the main thing separating one from the next.
			ctx.fillStyle = pieceTone(palette.base, palette.spread, random);
			ctx.fillRect(x, y, w, h);

			// Grain: long wavering strokes down the board's length. The single most identifiable wood
			// cue, and the reason a finished board still reads as timber. Broad, for pine's coarse bands,
			// but faint — the pattern should be legible without the floor becoming the busiest thing in frame.
			for (let g = 0; g < 26; g++) {
				const gx = x + random() * w;
				ctx.strokeStyle = `rgba(${palette.grain}, ${0.03 + random() * 0.07})`;
				ctx.lineWidth = 1 + random() * 3;
				ctx.beginPath();
				ctx.moveTo(gx, y);
				for (let gy = y; gy <= y + h; gy += 48) {
					ctx.lineTo(gx + (random() - 0.5) * 12, gy);
				}
				ctx.stroke();
			}

			// A knot or two. Concentric ellipses, because a knot is end-grain seen face on — this is
			// what says "wood" rather than "striped board" at a glance, and pine is knotty enough that
			// most boards should get one.
			if (random() > 0.3) {
				const kx = x + w * (0.25 + random() * 0.5);
				const ky = y + h * (0.15 + random() * 0.7);
				const kr = 5 + random() * 9;
				for (let ring = 5; ring >= 1; ring--) {
					ctx.strokeStyle = `rgba(${palette.knot}, ${0.04 + ring * 0.025})`;
					ctx.lineWidth = 1.5;
					ctx.beginPath();
					ctx.ellipse(kx, ky, kr * ring * 0.22, kr * ring * 0.34, 0.3, 0, Math.PI * 2);
					ctx.stroke();
				}
			}

			// Paint thins and dirt collects along the edges of every board.
			const edge = ctx.createLinearGradient(x, 0, x + w, 0);
			edge.addColorStop(0, `rgba(${palette.wear}, 0.3)`);
			edge.addColorStop(0.14, `rgba(${palette.wear}, 0)`);
			edge.addColorStop(0.86, `rgba(${palette.wear}, 0)`);
			edge.addColorStop(1, `rgba(${palette.wear}, 0.3)`);
			ctx.fillStyle = edge;
			ctx.fillRect(x, y, w, h);
			ctx.restore();

			// Relief: only the seams recede. Grain and knots change how a board looks, not its shape.
			bumpCtx.save();
			roundedRect(bumpCtx, x, y, w, h, 3);
			bumpCtx.clip();
			bumpCtx.fillStyle = '#dcdcdc';
			bumpCtx.fillRect(x, y, w, h);
			const shoulder = bumpCtx.createLinearGradient(x, 0, x + w, 0);
			shoulder.addColorStop(0, 'rgba(0, 0, 0, 0.5)');
			shoulder.addColorStop(0.08, 'rgba(0, 0, 0, 0)');
			shoulder.addColorStop(0.92, 'rgba(0, 0, 0, 0)');
			shoulder.addColorStop(1, 'rgba(0, 0, 0, 0.5)');
			bumpCtx.fillStyle = shoulder;
			bumpCtx.fillRect(x, y, w, h);
			bumpCtx.restore();
		}
	}

	// Wear across the whole floor, ignoring board boundaries — traffic does not respect the planking.
	addPatches(ctx, PLANK_W, PLANK_H, 6, 0.09, random);
	addGrain(ctx, PLANK_W, PLANK_H, 12, random);
	addGrain(bumpCtx, PLANK_W, PLANK_H, 8, random);

	const map = new CanvasTexture(colour);
	const bump = new CanvasTexture(relief);
	for (const texture of [map, bump]) {
		texture.wrapS = texture.wrapT = RepeatWrapping;
		texture.repeat.set(Math.max(1, Math.round(repeatX)), Math.max(1, Math.round(repeatY)));
		texture.anisotropy = 8;
	}
	map.colorSpace = SRGBColorSpace;
	return { map, bump };
}
